import { useAccount, useSession } from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn, useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useEffect, useMemo, useState } from 'react';

import type { AccountSetupMode } from './constants';

const ACCOUNT_SETUP_FEATURE_FLAG = 'RTRN-1507';

interface AccountSetupUserInfo {
  phone_number?: string | null;
  signup_reasons?: string[] | null;
}

export function getAccountSetupMode(
  userInfo?: AccountSetupUserInfo | null
): AccountSetupMode | null {
  if (!userInfo) {
    return null;
  }

  const hasPhone =
    typeof userInfo.phone_number === 'string' &&
    userInfo.phone_number.trim().length > 0;
  const hasSignupReasons =
    Array.isArray(userInfo.signup_reasons) &&
    userInfo.signup_reasons.length > 0;

  if (!hasPhone && hasSignupReasons) {
    return 'phone';
  }

  if (hasPhone && !hasSignupReasons) {
    return 'signupReason';
  }

  if (!hasPhone && !hasSignupReasons) {
    return 'both';
  }

  return null;
}

export function useAccountSetupModal() {
  const {
    session: { isAuthenticated },
  } = useSession();
  const { data: account, isLoading, isError } = useAccount(isAuthenticated);
  const queryClient = useQueryClient();
  const isInAccountSetupVariation = useFeatureIsOn(ACCOUNT_SETUP_FEATURE_FLAG);
  const [isDismissed, setIsDismissed] = useState(false);
  const [sessionMode, setSessionMode] = useState<AccountSetupMode | null>(null);

  const mode = useMemo(() => {
    if (isLoading || isError) {
      return null;
    }

    return getAccountSetupMode(account?.account?.user_info);
  }, [account, isLoading, isError]);

  useEffect(() => {
    if (isLoading || isError || isDismissed || mode === null) {
      return;
    }

    if (sessionMode === null) {
      setSessionMode(mode);
    }
  }, [mode, sessionMode, isDismissed, isLoading, isError]);

  const shouldShow = useMemo(() => {
    if (isDismissed || !isInAccountSetupVariation || sessionMode === null) {
      return false;
    }

    return true;
  }, [isDismissed, isInAccountSetupVariation, sessionMode]);

  const dismissModal = () => {
    setIsDismissed(true);
    setSessionMode(null);
  };

  return {
    isOpen: shouldShow,
    mode: sessionMode,
    onClose: dismissModal,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['account'] });
      dismissModal();
    },
  };
}
